import React, { useState, useEffect, useRef } from 'react';
import { collection, addDoc, query, orderBy, onSnapshot, where } from 'firebase/firestore';
import { db, auth } from '../firebaseConfig';
import Task from './Task';
import AppBar from './AppBar';
import { motion, AnimatePresence } from 'framer-motion';

function TaskList() {
  // State management for tasks
  const [tasks, setTasks] = useState([]);
  const [topics, setTopics] = useState([]);
  const [isSimilarityPanelVisible, setIsSimilarityPanelVisible] = useState(true);
  const [filters, setFilters] = useState({
    status: [],
    urgent: null,
    important: null,
    topics: []
  });
  const [prioritySort, setPrioritySort] = useState(true);

  // Add scroll container ref
  const scrollContainerRef = useRef(null);
  const newTaskRef = useRef(null);

  // Updated topics subscription to filter by current user
  useEffect(() => {
    const currentUser = auth.currentUser;
    if (!currentUser) return;

    const topicsQuery = query(
      collection(db, "topics"),
      where("uid", "==", currentUser.uid)
    );
    
    const unsubscribe = onSnapshot(topicsQuery, (snapshot) => {
      console.log(`[${new Date().toISOString()}] Topics listener triggered - ${snapshot.docs.length} docs`);
      const topicsData = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      setTopics(topicsData);
    });

    return () => unsubscribe();
  }, []);

  // Clean tasks subscription
  useEffect(() => {
    const currentUser = auth.currentUser;
    if (!currentUser) return;

    const q = query(
      collection(db, "tasks"),
      where("uid", "==", currentUser.uid)
    );
    
    const unsubscribe = onSnapshot(q, (querySnapshot) => {
      console.log(`[${new Date().toISOString()}] Tasks listener triggered - ${querySnapshot.docs.length} docs`);
      const tasksData = querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      setTasks(tasksData);
    });

    return () => unsubscribe();
  }, []);

  // Keep only this useEffect for scrolling
  useEffect(() => {
    if (scrollContainerRef.current && newTaskRef.current) {
      scrollContainerRef.current.scrollTop = scrollContainerRef.current.scrollHeight;
      newTaskRef.current = null; // Reset after scrolling
    }
  }, [tasks]);

  // Modify handleCreateTask to use newTaskRef
  const handleCreateTask = async (text = "") => {
    try {
      const currentUser = auth.currentUser;
      if (!currentUser) {
        console.error("No user is signed in");
        return;
      }

      const docRef = await addDoc(collection(db, "tasks"), {
        text: text,
        status: filters.status[0] || "doing",
        createdAt: new Date().toISOString(),
        urgency: filters.urgent ?? true,
        importance: filters.important ?? true,
        topics: filters.topics,
        uid: currentUser.uid
      });

      newTaskRef.current = docRef.id;
    } catch (error) {
      console.error("Error creating task:", error);
    }
  };

  // Add this new function to handle topic creation
  const handleCreateTopic = async () => {
    const topicName = prompt("Enter new topic name:");
    if (topicName?.trim()) {
      try {
        const currentUser = auth.currentUser;
        if (!currentUser) {
          console.error("No user is signed in"); 
          return;
        }

        await addDoc(collection(db, "topics"), {
          name: topicName.trim(),
          createdAt: new Date().toISOString(),
          uid: currentUser.uid
        });
      } catch (error) {
        console.error("Error creating topic:", error);
      }
    }
  };

  // Updated sorting logic with toggle between priority and date sorting
  const sortedTasks = tasks
    .sort((a, b) => {
      if (prioritySort) {
        // Status priority: doing (1), todo (2), done (3)
        const statusPriority = {
          'doing': 1,
          'todo': 2,
          'done': 3
        };
        
        // Compare status first
        const statusDiff = statusPriority[a.status] - statusPriority[b.status];
        if (statusDiff !== 0) return statusDiff;

        // Then compare importance/urgency combinations
        // Priority: both (1), important only (2), urgent only (3), neither (4)
        const getImportanceUrgencyPriority = (task) => {
          if (task.importance && task.urgency) return 1;
          if (task.importance) return 3;
          if (task.urgency) return 2;
          return 4;
        };

        const priorityDiff = getImportanceUrgencyPriority(a) - getImportanceUrgencyPriority(b);
        if (priorityDiff !== 0) return priorityDiff;

        // If all other factors are equal, sort by date (newest first)
        return new Date(b.createdAt) - new Date(a.createdAt);
      } else {
        // Simple date sort (newest first)
        return new Date(b.createdAt) - new Date(a.createdAt);
      }
    });

  // Apply filters to sorted tasks
  const filteredAndSortedTasks = sortedTasks
    .filter(task => filters.status.length === 0 || filters.status.includes(task.status))
    .filter(task => {
      if (filters.important === null && filters.urgent === null) {
        return true;
      }
      if (filters.important !== null && filters.urgent === null) {
        return task.importance === filters.important;
      }
      if (filters.urgent !== null && filters.important === null) {
        return task.urgency === filters.urgent;
      }
      return task.importance === filters.important && task.urgency === filters.urgent;
    })
    .filter(task => filters.topics.length === 0 || 
      task.topics?.some(topicId => filters.topics.includes(topicId)));

  const toggleFilter = (type, value) => {
    if (type === 'status') {
      setFilters(prev => ({
        ...prev,
        status: prev.status.includes(value)
          ? prev.status.filter(s => s !== value)
          : [...prev.status, value]
      }));
    } else {
      setFilters(prev => ({
        ...prev,
        [type]: prev[type] === value ? null : value
      }));
    }
  };

  const toggleTopicFilter = (topicId) => {
    setFilters(prev => ({
      ...prev,
      topics: prev.topics.includes(topicId)
        ? prev.topics.filter(id => id !== topicId)
        : [...prev.topics, topicId]
    }));
  };

  return (
    <div className="flex-col h-[100dvh] overflow-hidden">
      <div className="flex bg-gray-50 h-[100dvh]">
        {/* Main tasks panel */}
        <div className="flex-1 max-w-[440px] border-r border-gray-200 bg-white flex flex-col pb-[var(--app-bar-height,120px)]">
          {/* Title and Filters */}

          {/* Tasks list */}
          <div 
            ref={scrollContainerRef}
            className="flex-1 overflow-y-auto flex flex-col-reverse"
          >
            <AnimatePresence mode="popLayout">
              {filteredAndSortedTasks.map(task => (
                <motion.div
                  key={task.id}
                  layout
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  transition={{ 
                    layout: {
                      type: "spring",
                      stiffness: 500,
                      damping: 40
                    },
                    opacity: { duration: 0.15 }
                  }}
                >
                  <Task
                    task={task}
                    topics={topics}
                    onTaskChange={() => {}}
                  />
                </motion.div>
              ))}
            </AnimatePresence>
          </div>
        </div>
      </div>

      {/* AppBar */}
      <AppBar
        className="fixed bottom-0 left-0 right-0"
        onSubmit={(text) => handleCreateTask(text)}
        onCreateNote={handleCreateTask}
        isSimilarityPanelVisible={isSimilarityPanelVisible}
        onToggleSimilarityPanel={() => setIsSimilarityPanelVisible(!isSimilarityPanelVisible)}
        filters={filters}
        topics={topics}
        onToggleFilter={toggleFilter}
        onToggleTopicFilter={toggleTopicFilter}
        onCreateTopic={handleCreateTopic}
        prioritySort={prioritySort}
        onTogglePrioritySort={() => setPrioritySort(!prioritySort)}
      />
    </div>
  );
}

export default TaskList; 